/**
 * stageDoneSummary.ts — Stage 完成摘要（对齐 CC Agent Done 行）
 *
 * 职责：
 *     从 scoped loop tree 统计 tool uses / duration / agent turn；提取 text teaser。
 *
 * 链路位置：
 *     WorkflowStageContainer 完成态显示（P2+）。
 *
 * 当前裁剪范围：
 *     不含 React；仅纯函数统计。
 */
import type { SessionTree } from '../../types/tree';

export interface StageDoneSummary {
  label: string;
  teaser: string;
  extraLines: number;
}

function countToolUses(tree: SessionTree): number {
  return Object.values(tree.byId).filter((node) => node.kind === 'tool_call').length;
}

function computeDurationSeconds(tree: SessionTree): number | null {
  const started = tree.meta.startedAt;
  if (started == null) return null;

  let maxTs = started;
  for (const node of Object.values(tree.byId)) {
    if ('endedAt' in node && typeof node.endedAt === 'number') {
      maxTs = Math.max(maxTs, node.endedAt);
    }
    if ('startedAt' in node && typeof node.startedAt === 'number') {
      maxTs = Math.max(maxTs, node.startedAt);
    }
  }

  const seconds = Math.round((maxTs - started) / 1000);
  return seconds > 0 ? seconds : null;
}

function lastTextContent(tree: SessionTree): string {
  const textNodes = Object.values(tree.byId).filter((node) => node.kind === 'text');
  if (textNodes.length === 0) return '';
  return textNodes[textNodes.length - 1]!.accumulated.trim();
}

/** 从 loop tree 构建 Stage Done 摘要 + 首行 teaser（非 display.description）。 */
export function buildStageDoneSummary(tree: SessionTree): StageDoneSummary | null {
  if (tree.status !== 'done' && tree.status !== 'error') return null;

  const toolUses = countToolUses(tree);
  const duration = computeDurationSeconds(tree);
  const durationSuffix = duration != null ? ` · ${duration}s` : '';

  const label =
    toolUses > 0
      ? `Done (${toolUses} tool use${toolUses === 1 ? '' : 's'}${durationSuffix})`
      : `Done (1 agent turn${durationSuffix})`;

  const fullText = lastTextContent(tree);
  const lines = fullText.split('\n').map((line) => line.trim()).filter(Boolean);
  const teaser = lines[0] ?? '';
  const extraLines = Math.max(0, lines.length - 1);

  return { label, teaser, extraLines };
}

export function formatTeaserExpandHint(extraLines: number, verbose: boolean): string | null {
  if (extraLines <= 0) return null;
  const shown = verbose ? Math.min(extraLines, 2) : 0;
  const hidden = extraLines - shown;
  if (hidden <= 0) return null;
  return `… +${hidden} lines (ctrl+o to expand)`;
}
